import { Head, Link, router, usePage } from "@inertiajs/react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import {
    Box,
    Container,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import { ElementType } from "react"
import { useTranslation } from "react-i18next"

interface IMonthlyLogEntry {
    employee_id: number
    employee_name: string
    worked_hours: number
    extra_hours: number
    double_hours: number
}

type Props = {
    entries: IMonthlyLogEntry[]
    date: string
}

export default function MonthlyLogPage() {
    const { entries, date } = usePage<Props>().props
    const { t } = useTranslation()
    const currentDate = dayjs(date)

    const handleDateChange = (newDate: Dayjs | null) => {
        if (!newDate) return
        router.get(
            "/weekly_reports/monthly_log",
            { date: newDate.startOf("month").format("YYYY-MM-DD") },
            { preserveState: false }
        )
    }

    const totalWorked = entries.reduce((sum, e) => sum + e.worked_hours, 0)
    const totalExtra  = entries.reduce((sum, e) => sum + e.extra_hours, 0)
    const totalDouble = entries.reduce((sum, e) => sum + e.double_hours, 0)

    return (
        <>
            <Head title={t("weeklyReports.monthlyLogTitle")} />

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* ── Header ── */}
                <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1.5, sm: 1.5 }, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                        <Tooltip title={t("weeklyReports.monthlyLogPage.backTooltip")}>
                            <IconButton component={Link as ElementType} href="/weekly_reports" size="small">
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {t("weeklyReports.monthlyLogTitle")}
                        </Typography>
                    </Box>
                    <DatePicker
                        views={["month", "year"]}
                        label={t("weeklyReports.monthlyLogPage.monthAndYear")}
                        value={currentDate}
                        onChange={handleDateChange}
                        disableFuture
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                </Box>

                {/* ── Table ── */}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>{t("common.employee")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("weeklyReports.monthlyLogPage.workedHrs")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("weeklyReports.monthlyLogPage.extraHrs")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("weeklyReports.monthlyLogPage.doubleHrs")}</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.length > 0 ? (
                                <>
                                    {entries.map((entry) => (
                                        <TableRow key={entry.employee_id} hover>
                                            <TableCell>{entry.employee_name}</TableCell>
                                            <TableCell align="right">{entry.worked_hours.toFixed(2)}</TableCell>
                                            <TableCell align="right">{entry.extra_hours.toFixed(2)}</TableCell>
                                            <TableCell align="right">{entry.double_hours.toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ "& td": { fontWeight: "bold", borderTop: "2px solid", borderColor: "divider" } }}>
                                        <TableCell>{t("common.total")}</TableCell>
                                        <TableCell align="right">{totalWorked.toFixed(2)}</TableCell>
                                        <TableCell align="right">{totalExtra.toFixed(2)}</TableCell>
                                        <TableCell align="right">{totalDouble.toFixed(2)}</TableCell>
                                    </TableRow>
                                </>
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                        {t("weeklyReports.monthlyLogPage.noData", { month: currentDate.format("MMMM YYYY") })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </>
    )
}
